// utils/fetchWrapper.js
import { getAuthToken, setCsrfToken } from './securityUtils';
import { logout } from './auth';

// Store the original fetch function to avoid recursion
const originalFetch = window.fetch;

// Track if we're currently refreshing the CSRF token to avoid loops
let isRefreshingCsrf = false;

/**
 * Fetch a fresh CSRF token from the server
 * @returns {Promise<string|null>} - The new CSRF token or null on failure
 */
async function refreshCsrfToken() {
  if (isRefreshingCsrf) {
    return null;
  }
  
  isRefreshingCsrf = true;
  
  try {
    const baseUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000';
    const response = await originalFetch(`${baseUrl}/api/auth/csrf-token`, {
      method: 'GET',
      credentials: 'include'
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.csrfToken) {
        setCsrfToken(data.csrfToken);
        console.log('CSRF token refreshed successfully');
        return data.csrfToken;
      }
    }
  } catch (error) {
    console.error('Failed to refresh CSRF token:', error);
  } finally {
    isRefreshingCsrf = false;
  }
  
  return null;
}

/**
 * Get the current CSRF token from available sources
 * @returns {string|null} - The CSRF token or null
 */
function getCurrentCsrfToken() {
  // Use the CSRF token from localStorage if available
  let csrfToken = localStorage.getItem('csrfToken');
  if (csrfToken) {
    return csrfToken;
  }
  
  // Try to get from _csrfToken cookie (set by backend for SPA use)
  let match = document.cookie.match(/(?:^|;)\s*_csrfToken=([^;]*)/);
  if (match && match[1]) {
    return match[1];
  }
  
  // Fallback to _csrf cookie (legacy, not recommended for SPA)
  match = document.cookie.match(/(?:^|;)\s*_csrf=([^;]*)/);
  if (match && match[1]) {
    return match[1];
  }
  
  return null;
}

/**
 * Enhanced fetch wrapper with authentication, CSRF protection, and error handling
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options
 * @param {boolean} isRetry - Whether this is a retry after CSRF refresh
 * @returns {Promise<Response>} - Fetch response
 */
async function fetchWrapper(url, options = {}, isRetry = false) {
  // Determine the base URL based on environment
  const baseUrl = process.env.NODE_ENV === 'production' 
    ? '' // In production, use relative URLs
    : 'http://localhost:3000'; // In development, use localhost
    
  const newUrl = url.startsWith('http') ? url : baseUrl + url;
  
  // Initialize headers if not present
  if (!options.headers) {
    options.headers = {};
  }
  
  // Add CSRF token to non-GET requests for API routes in production mode
  if (
    options.method &&
    options.method.toUpperCase() !== 'GET' &&
    process.env.NODE_ENV === 'production' &&
    newUrl.includes('/api/')
  ) {
    const csrfToken = getCurrentCsrfToken();
    if (csrfToken) {
      options.headers['X-CSRF-Token'] = csrfToken;
    }
  }
  
  // Add authentication token if available
  const token = getAuthToken();
  if (token) {
    options.headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Ensure credentials are included for cookie handling
  options.credentials = 'include';
  
  try {
    const response = await originalFetch(newUrl, options);
    
    // Check for authentication errors
    if (response.status === 401) {
      // Use the auth.js logout function to properly clear auth state
      logout(false); // Don't try to call the server again
      
      // Redirect to login if on admin page
      if (window.location.pathname.startsWith('/admin') && 
          window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
      return response;
    }
    
    // Check for CSRF errors (403 with specific message)
    if (response.status === 403 && !isRetry) {
      // Clone the response so we can read it and still return it if needed
      const clonedResponse = response.clone();
      
      try {
        const errorData = await clonedResponse.json();
        
        // Check if this is a CSRF error
        if (errorData.message && errorData.message.toLowerCase().includes('csrf') ||
            errorData.message && errorData.message.toLowerCase().includes('form submission')) {
          console.warn('CSRF token invalid or expired, attempting to refresh...');
          
          // Try to refresh the CSRF token
          const newToken = await refreshCsrfToken();
          
          if (newToken) {
            // Retry the original request with the new token
            console.log('Retrying request with new CSRF token');
            return fetchWrapper(url, { ...options, headers: { ...options.headers } }, true);
          }
        }
      } catch (parseError) {
        // Response wasn't JSON, continue with normal 403 handling
      }
      
      console.error('Permission denied or CSRF validation failed');
      
      // If on admin page and not already on home, redirect to admin home
      if (window.location.pathname.startsWith('/admin') && 
          window.location.pathname !== '/admin/home' &&
          window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/home';
      }
    }
    
    return response;
  } catch (error) {
    console.error('Network or fetch error:', error);
    throw error;
  }
}

export default fetchWrapper;
export { refreshCsrfToken };
